import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { toast } from 'react-toastify';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import { API_BASE_URL } from '../../../config/api';


const HREmployeesPendingGoals = () => {
  const baseUrl = API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;
  const [expandedTexts, setExpandedTexts] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectGoal, setRejectGoal] = useState(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [rejectError, setRejectError] = useState(null);

  const handleViewGoal = (goal) => {
    setSelectedGoal(goal);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedGoal(null);
  };

  const toggleTextExpansion = (goalId, field) => {
    setExpandedTexts(prev => ({
      ...prev,
      [`${goalId}-${field}`]: !prev[`${goalId}-${field}`]
    }));
  };

  const renderTruncatedText = (text, goalId, field, maxLength = 40) => {
    const safeText = (text ?? '').toString();
    if (!safeText) return '';
    if (safeText.length <= maxLength) return safeText;

    const key = `${goalId || 'unknown'}-${field}`;
    const isExpanded = !!expandedTexts[key];
    const shownText = isExpanded ? safeText : `${safeText.slice(0, maxLength)}...`;

    return (
      <span>
        <span style={{ whiteSpace: 'pre-wrap' }}>{shownText}</span>
        <button
          type="button"
          onClick={() => toggleTextExpansion(goalId || 'unknown', field)}
          className="btn btn-link btn-sm p-0 ms-2 text-decoration-none fw-semibold"
        >
          {isExpanded ? 'Read less' : 'Read more'}
        </button>
      </span>
    );
  };

  const handleApproveGoal = async (goal) => {
    setApprovalLoading(goal.id);
    try {
      const token = Cookies.get('token');
      await axios.post(`${baseUrl}/api/goals/${goal.id}/approval/`, { action: 'approve' }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      toast.success('Goal approved!');
      
      const response = await axios.get(`${baseUrl}/api/goals/pending/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const pendingGoals = Array.isArray(response.data) ? response.data.filter(goal => goal.approval_status === 'pending') : [];
      setGoals(pendingGoals);
    } catch (err) {
      console.log(err);
      toast.error(err.message || 'Failed to approve goal');
    } finally {
      setApprovalLoading(null);
    }
  };

  const openRejectModal = (goal) => {
    setRejectGoal(goal);
    setRejectReason('');
    setRejectError(null);
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setRejectModalOpen(false);
    setRejectGoal(null);
    setRejectReason('');
    setRejectError(null);
  };

  const handleRejectSubmit = async (e) => {
    e.preventDefault();
    setRejectSubmitting(true);
    setRejectError(null);
    try {
      const token = Cookies.get('token');
      await axios.post(`${baseUrl}/api/goals/${rejectGoal.id}/approval/`, { action: 'reject', reason: rejectReason }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      toast.success('Goal rejected!');
      
      const response = await axios.get(`${baseUrl}/api/goals/pending/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const pendingGoals = Array.isArray(response.data) ? response.data.filter(goal => goal.approval_status === 'pending') : [];
      setGoals(pendingGoals);
      closeRejectModal();
    } catch (err) {
      setRejectError(err.message || 'Failed to reject goal');
    } finally {
      setRejectSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchPendingGoals = async () => {
      setLoading(true);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/goals/pending/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const pendingGoals = Array.isArray(response.data) ? response.data.filter(goal => goal.approval_status === 'pending') : [];
        setGoals(pendingGoals);
      } catch (err) {
        toast.error('Failed to fetch pending goals.');
      } finally {
        setLoading(false);
      }
    };
    fetchPendingGoals();
  }, [baseUrl]);

  useEffect(() => {
    setCurrentPage(1);
  }, [goals]);

  const totalPages = Math.ceil(goals.length / rowsPerPage) || 1;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentGoals = goals.slice(indexOfFirstRow, indexOfLastRow);

  const getKraLabel = (goal) => {
    const name = goal?.key_result_area?.name || goal?.name || goal?.key_result_area;
    return (name ?? 'Uncategorized').toString();
  };

  const sortedCurrentGoals = [...currentGoals].sort((a, b) => {
    const an = (a?.employee_name || '').toString();
    const bn = (b?.employee_name || '').toString();
    const empCompare = an.localeCompare(bn, undefined, { sensitivity: 'base' });
    if (empCompare !== 0) return empCompare;

    const ak = getKraLabel(a);
    const bk = getKraLabel(b);
    const kraCompare = ak.localeCompare(bk, undefined, { sensitivity: 'base' });
    if (kraCompare !== 0) return kraCompare;

    const aa = (a?.activity || '').toString();
    const ba = (b?.activity || '').toString();
    return aa.localeCompare(ba, undefined, { sensitivity: 'base' });
  });

  const currentPageCountsByEmployee = sortedCurrentGoals.reduce((acc, g) => {
    const key = (g?.employee_name || 'Unknown Employee').toString();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Pending KPIs</h4>
              <p className="text-muted mb-0 fs-13">View and approve all pending KPIs for your department</p>
            </div>
          </div>

          {/* Main Card */}
          <div className="card shadow-sm mb-4">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Activity</th>
                    <th>Stakeholder</th>
                    <th style={{ width: '120px' }}>RAG</th>
                    <th>Quarter</th>
                    <th>Metric</th>
                    <th>Status</th>
                    <th className="text-center" style={{ width: '140px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted fs-13 mt-2 mb-0">Loading pending KPIs...</p>
                      </td>
                    </tr>
                  ) : goals.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-5 text-muted">
                        <i className="ti ti-target-off fs-36 d-block mb-2 text-secondary opacity-50" />
                        No pending KPIs found.
                      </td>
                    </tr>
                  ) : (
                    sortedCurrentGoals.map((goal, idx) => {
                      const employeeName = (goal?.employee_name || 'Unknown Employee').toString();
                      const prevEmployeeName = idx > 0
                        ? (sortedCurrentGoals[idx - 1]?.employee_name || 'Unknown Employee').toString()
                        : null;
                      const showEmployeeHeader = idx === 0 || employeeName !== prevEmployeeName;

                      const kraLabel = getKraLabel(goal);
                      const prevKraLabel = idx > 0 ? getKraLabel(sortedCurrentGoals[idx - 1]) : null;
                      const showKraHeader = showEmployeeHeader || kraLabel !== prevKraLabel;

                      return (
                        <React.Fragment key={goal.id || `${employeeName}-${idx}`}>
                          {showEmployeeHeader && (
                            <tr className="table-secondary">
                              <td colSpan={7} className="fw-bold py-2 text-dark">
                                <i className="ti ti-user me-1" />
                                {employeeName}
                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle ms-2 fw-semibold">
                                  {currentPageCountsByEmployee[employeeName]} pending
                                </span>
                              </td>
                            </tr>
                          )}

                          {showKraHeader && (
                            <tr className="bg-light">
                              <td colSpan={7} className="fw-bold py-2 ps-4 text-secondary">
                                {(() => {
                                  const kraPercentage =
                                    goal?.key_result_area?.percentage ??
                                    goal?.kra?.percentage ??
                                    goal?.percentage;
                                  return (
                                    <>
                                      KRA: <span className="text-dark">{kraLabel}</span>
                                      <span className="badge bg-secondary-subtle text-secondary ms-2 fw-semibold">
                                        Weight: {kraPercentage !== undefined && kraPercentage !== null ? `${kraPercentage}%` : '-'}
                                      </span>
                                    </>
                                  );
                                })()}
                              </td>
                            </tr>
                          )}

                          <tr>
                            <td>
                              {renderTruncatedText(
                                goal.activity,
                                goal.id || `${idx + indexOfFirstRow}`,
                                'activity',
                                55
                              )}
                            </td>
                            <td>{goal.stakeholder}</td>
                            <td>
                              <span
                                className="badge w-100 text-white"
                                style={{ backgroundColor: goal.progress_indicator || '#6c757d' }}
                              >
                                {goal.rag_color || 'N/A'}
                              </span>
                            </td>
                            <td>{goal.quarter}</td>
                            <td>{goal.metric}%</td>
                            <td>
                              <span className="badge bg-warning-subtle text-warning border border-warning-subtle d-inline-flex align-items-center gap-1">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Pending
                              </span>
                            </td>
                            <td className="text-center">
                              <div className="d-flex align-items-center justify-content-center gap-1">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-light text-primary"
                                  title="View KPI Details"
                                  onClick={() => handleViewGoal(goal)}
                                >
                                  <i className="ti ti-eye" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-light text-success"
                                  title="Approve KPI"
                                  onClick={() => handleApproveGoal(goal)}
                                  disabled={approvalLoading === goal.id}
                                >
                                  {approvalLoading === goal.id ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                  ) : (
                                    <i className="ti ti-circle-check" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-icon btn-light text-danger"
                                  title="Reject KPI"
                                  onClick={() => openRejectModal(goal)}
                                >
                                  <i className="ti ti-circle-x" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {!loading && goals.length > rowsPerPage && (
              <div className="card-footer d-flex align-items-center justify-content-between py-2 px-3 border-top">
                <span className="text-muted fs-13">
                  Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, goals.length)} of {goals.length} goals
                </span>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn btn-sm btn-light"
                  >
                    <i className="ti ti-chevron-left me-1" />
                    Previous
                  </button>
                  <span className="fs-13 fw-semibold px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn btn-sm btn-light"
                  >
                    Next
                    <i className="ti ti-chevron-right ms-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>

      {/* View Details Modal */}
      {modalOpen && selectedGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">KPI Details</h5>
                <button type="button" className="btn-close" onClick={closeModal} aria-label="Close" />
              </div>
              <div className="modal-body">
                <div className="table-responsive border rounded">
                  <table className="table table-striped mb-0">
                    <tbody>
                      <tr>
                        <th style={{ width: '35%' }} className="text-muted fw-semibold fs-14">Key Result Area:</th>
                        <td className="fw-medium text-dark">{selectedGoal.key_result_area?.name || selectedGoal.name}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Key Result Area Weight:</th>
                        <td className="fw-medium text-dark">{selectedGoal.key_result_area?.percentage || selectedGoal.percentage}%</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Activity:</th>
                        <td className="fw-medium text-dark" style={{ whiteSpace: 'pre-wrap' }}>{selectedGoal.activity}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">KPI Metric:</th>
                        <td className="fw-medium text-dark">{selectedGoal.metric}%</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Stakeholder:</th>
                        <td className="fw-medium text-dark">{selectedGoal.stakeholder}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Quarter:</th>
                        <td className="fw-medium text-dark">{selectedGoal.quarter}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">RAG Indicator:</th>
                        <td>
                          <span
                            className="badge text-white px-3 py-2"
                            style={{ backgroundColor: selectedGoal.progress_indicator || '#6c757d' }}
                          >
                            {selectedGoal.rag_color || 'N/A'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Completion Status:</th>
                        <td className="fw-medium text-dark">
                          {selectedGoal.status
                            ? selectedGoal.status.charAt(0).toUpperCase() + selectedGoal.status.slice(1).replace(/_/g, ' ')
                            : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Is Overdue:</th>
                        <td>
                          {selectedGoal.is_overdue ? (
                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle">Yes</span>
                          ) : (
                            <span className="badge bg-success-subtle text-success border border-success-subtle">No</span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Approval Status:</th>
                        <td>
                          <span className="badge bg-warning-subtle text-warning border border-warning-subtle d-inline-flex align-items-center gap-1">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            Pending
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-semibold fs-14">Due Date:</th>
                        <td className="fw-medium text-dark">
                          {selectedGoal.completion_date
                            ? new Date(selectedGoal.completion_date).toLocaleDateString('en-GB')
                            : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Reject KPI</h5>
                <button type="button" className="btn-close" onClick={closeRejectModal} aria-label="Close" />
              </div>
              <form onSubmit={handleRejectSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-medium">
                      Rejection Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      name="rejection_reason"
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      required
                      className="form-control"
                      placeholder="Enter reason for rejection..."
                      rows={4}
                      disabled={rejectSubmitting}
                    />
                  </div>
                  {rejectError && (
                    <div className="alert alert-danger py-2 mb-0" role="alert">
                      {rejectError}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" onClick={closeRejectModal} className="btn btn-secondary" disabled={rejectSubmitting}>
                    Close
                  </button>
                  <button type="submit" className="btn btn-danger" disabled={rejectSubmitting}>
                    {rejectSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Rejecting...
                      </>
                    ) : (
                      'Reject KPI'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HREmployeesPendingGoals;
